import type { EChartsOption } from 'echarts';
import * as echarts from 'echarts/core';
import type { TestMeasureId } from '../test-measures/test-measures.model';
import type { TestResult } from './test-runner.model';
import { tPdf, chiSquaredPdf, binomialPmf, binomialCdf } from './distributions.util';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildTestResultChartOptions(result: TestResult, highlight?: TestMeasureId | null): EChartsOption {
  const h = highlight ?? null;
  switch (result.distributionType) {
    case 't':
      return buildTDistributionChart(result, h);
    case 'chi-squared':
      return buildChiSquaredChart(result, h);
    case 'binomial':
      return buildBinomialChart(result, h);
  }
}

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

const COLOR_PVALUE_AREA = 'rgba(139, 92, 246, 0.25)';
const COLOR_PVALUE_AREA_BRIGHT = 'rgba(139, 92, 246, 0.4)';
const COLOR_CRITICAL_LINE = '#ef4444';
const COLOR_ACCEPT = 'rgba(156, 163, 175, 0.06)';
const COLOR_CURVE = '#6b7280';
const COLOR_TEST_STAT = '#0ea5e9';
const COLOR_LABEL_MUTED = '#9ca3af';

// Emphasized variants – used when a measure is highlighted (slightly bolder, no dimming)
const COLOR_TEST_STAT_BRIGHT = '#0284c7';

// ---------------------------------------------------------------------------
// Shared chart config helpers
// ---------------------------------------------------------------------------

function densityYAxis(): Record<string, unknown> {
  return {
    type: 'value',
    name: 'Density',
    nameLocation: 'middle',
    nameGap: 35,
    nameTextStyle: { fontSize: 11, color: COLOR_LABEL_MUTED },
    axisLine: { show: true, lineStyle: { color: '#d1d5db' } },
    axisTick: { show: false },
    splitLine: { show: false },
    axisLabel: { show: false },
  };
}

const CHART_GRID = { left: 50, right: 20, top: 50, bottom: 40 };

// ---------------------------------------------------------------------------
// t-distribution chart
// ---------------------------------------------------------------------------

function buildTDistributionChart(result: TestResult, highlight: TestMeasureId | null): EChartsOption {
  const { testStatistic: tStat, degreesOfFreedom: df, criticalValues, tailType, alpha } = result;
  const h = highlight;

  const xMin = -5;
  const xMax = 5;
  const nPoints = 300;
  const peakY = tPdf(0, df);

  const allPoints: Array<[number, number]> = [];
  for (let i = 0; i <= nPoints; i++) {
    const x = xMin + (i / nPoints) * (xMax - xMin);
    allPoints.push([x, tPdf(x, df)]);
  }

  const rejectColor = h === 'pValue' || h === 'criticalValue' ? COLOR_PVALUE_AREA_BRIGHT : COLOR_PVALUE_AREA;
  const series = buildSplitSeries(allPoints, criticalValues, tailType, rejectColor);

  // Mark lines
  const markLineData: Array<Record<string, unknown>> = [];

  const cvLineWidth = h === 'criticalValue' ? 2.5 : 1.5;
  const cvFontSize = h === 'criticalValue' ? 12 : 10;

  for (const cv of criticalValues) {
    markLineData.push({
      xAxis: cv,
      lineStyle: { color: COLOR_CRITICAL_LINE, type: 'dashed' as const, width: cvLineWidth },
      label: {
        formatter: `t* = ${cv.toFixed(3)}`,
        position: 'insideEndBottom' as const,
        fontSize: cvFontSize,
        color: COLOR_CRITICAL_LINE,
      },
    });
  }

  // Test statistic — clamp to chart range for the markLine
  const tLineColor = h === 'testStatistic' ? COLOR_TEST_STAT_BRIGHT : COLOR_TEST_STAT;
  const tLineWidth = h === 'testStatistic' ? 3 : 2;
  const tFontSize = h === 'testStatistic' ? 12 : 10;

  const clampedT = Math.max(xMin, Math.min(xMax, tStat));
  markLineData.push({
    xAxis: clampedT,
    lineStyle: { color: tLineColor, type: 'solid' as const, width: tLineWidth },
    label: {
      formatter: `t = ${tStat.toFixed(3)}`,
      position: 'insideEndTop' as const,
      fontSize: tFontSize,
      fontWeight: 600,
      color: tLineColor,
    },
  });

  if (series.length > 0) {
    (series[0] as Record<string, unknown>)['markLine'] = {
      symbol: 'none',
      animation: false,
      data: markLineData,
    };
  }

  // Annotation series for region labels
  const annotations = buildAnnotationSeries(criticalValues, tailType, alpha, xMin, xMax, peakY);
  series.push(...annotations);

  const titleStyle =
    h === 'degreesOfFreedom'
      ? { fontSize: 14, fontWeight: 700 as const, color: '#1f2937' }
      : { fontSize: 13, fontWeight: 600 as const, color: '#374151' };

  return {
    title: {
      text: `t-distribution, df = ${Number.isInteger(df) ? df : df.toFixed(1)}`,
      left: 'center',
      top: 6,
      textStyle: titleStyle,
    },
    grid: CHART_GRID,
    xAxis: {
      type: 'value' as const,
      min: xMin,
      max: xMax,
      axisLine: { lineStyle: { color: '#d1d5db' } },
      axisTick: { lineStyle: { color: '#d1d5db' } },
      axisLabel: { fontSize: 10, color: COLOR_LABEL_MUTED },
      splitLine: { show: false },
    },
    yAxis: densityYAxis(),
    series,
    animation: false,
  };
}

// ---------------------------------------------------------------------------
// Chi-squared distribution chart
// ---------------------------------------------------------------------------

function buildChiSquaredChart(result: TestResult, highlight: TestMeasureId | null): EChartsOption {
  const { testStatistic: x2, degreesOfFreedom: df, criticalValues, alpha } = result;
  const h = highlight;

  const xMin = 0;
  const xMax = Math.max(df * 3, x2 * 1.5, criticalValues[0] * 1.5, 10);
  const nPoints = 300;

  const allPoints: Array<[number, number]> = [];
  let peakY = 0;
  for (let i = 0; i <= nPoints; i++) {
    const x = xMin + (i / nPoints) * (xMax - xMin);
    const y = chiSquaredPdf(x, df);
    allPoints.push([x, y]);
    if (y > peakY) {
      peakY = y;
    }
  }

  const rejectColor = h === 'pValue' || h === 'criticalValue' ? COLOR_PVALUE_AREA_BRIGHT : COLOR_PVALUE_AREA;
  const series = buildSplitSeries(allPoints, criticalValues, 'right', rejectColor);

  const markLineData: Array<Record<string, unknown>> = [];

  const cvLineWidth = h === 'criticalValue' ? 2.5 : 1.5;
  const cvFontSize = h === 'criticalValue' ? 12 : 10;

  if (criticalValues.length > 0) {
    markLineData.push({
      xAxis: criticalValues[0],
      lineStyle: { color: COLOR_CRITICAL_LINE, type: 'dashed' as const, width: cvLineWidth },
      label: {
        formatter: `\u03C7\u00B2* = ${criticalValues[0].toFixed(3)}`,
        position: 'insideEndBottom' as const,
        fontSize: cvFontSize,
        color: COLOR_CRITICAL_LINE,
      },
    });
  }

  const statLineColor = h === 'testStatistic' ? COLOR_TEST_STAT_BRIGHT : COLOR_TEST_STAT;
  const statLineWidth = h === 'testStatistic' ? 3 : 2;
  const statFontSize = h === 'testStatistic' ? 12 : 10;

  markLineData.push({
    xAxis: x2,
    lineStyle: { color: statLineColor, type: 'solid' as const, width: statLineWidth },
    label: {
      formatter: `\u03C7\u00B2 = ${x2.toFixed(3)}`,
      position: 'insideEndTop' as const,
      fontSize: statFontSize,
      fontWeight: 600,
      color: statLineColor,
    },
  });

  if (series.length > 0) {
    (series[0] as Record<string, unknown>)['markLine'] = {
      symbol: 'none',
      animation: false,
      data: markLineData,
    };
  }

  // Annotation series
  const annotations = buildAnnotationSeries(criticalValues, 'right', alpha, xMin, xMax, peakY);
  series.push(...annotations);

  const titleStyle =
    h === 'degreesOfFreedom'
      ? { fontSize: 14, fontWeight: 700 as const, color: '#1f2937' }
      : { fontSize: 13, fontWeight: 600 as const, color: '#374151' };

  return {
    title: {
      text: `\u03C7\u00B2 distribution, df = ${df}`,
      left: 'center',
      top: 6,
      textStyle: titleStyle,
    },
    grid: CHART_GRID,
    xAxis: {
      type: 'value' as const,
      min: xMin,
      max: xMax,
      axisLine: { lineStyle: { color: '#d1d5db' } },
      axisTick: { lineStyle: { color: '#d1d5db' } },
      axisLabel: { fontSize: 10, color: COLOR_LABEL_MUTED },
      splitLine: { show: false },
    },
    yAxis: densityYAxis(),
    series,
    animation: false,
  };
}

// ---------------------------------------------------------------------------
// Binomial chart (sign test)
// ---------------------------------------------------------------------------

function buildBinomialChart(result: TestResult, highlight: TestMeasureId | null): EChartsOption {
  const n = result.degreesOfFreedom;
  const observed = result.testStatistic;
  const { tailType, alpha } = result;
  const h = highlight;

  const categories: string[] = [];
  const barData: Array<{ value: number; itemStyle: { color: string } }> = [];

  for (let k = 0; k <= n; k++) {
    categories.push(String(k));
    const pmf = binomialPmf(k, n, 0.5);
    const inReject = isBinomialReject(k, n, alpha, tailType);
    const isObserved = k === observed;

    let color: string;
    if (isObserved) {
      color = h === 'testStatistic' ? COLOR_TEST_STAT_BRIGHT : COLOR_TEST_STAT;
    } else if (inReject) {
      color = COLOR_CRITICAL_LINE;
    } else {
      color = '#d1d5db';
    }

    barData.push({ value: pmf, itemStyle: { color } });
  }

  const titleStyle =
    h === 'degreesOfFreedom'
      ? { fontSize: 14, fontWeight: 700 as const, color: '#1f2937' }
      : { fontSize: 13, fontWeight: 600 as const, color: '#374151' };

  return {
    title: {
      text: `Binomial(${n}, 0.5)`,
      left: 'center',
      top: 6,
      textStyle: titleStyle,
    },
    grid: CHART_GRID,
    xAxis: {
      type: 'category' as const,
      data: categories,
      axisLine: { lineStyle: { color: '#d1d5db' } },
      axisTick: { lineStyle: { color: '#d1d5db' } },
      axisLabel: { fontSize: 10, color: COLOR_LABEL_MUTED },
    },
    yAxis: densityYAxis(),
    series: [
      {
        type: 'bar' as const,
        data: barData,
        barMaxWidth: 20,
      },
    ],
    animation: false,
  };
}

function isBinomialReject(k: number, n: number, alpha: number, tailType: string): boolean {
  const pLeft = binomialCdf(k, n, 0.5);
  const pRight = 1 - binomialCdf(k - 1, n, 0.5);

  switch (tailType) {
    case 'left':
      return pLeft <= alpha;
    case 'right':
      return pRight <= alpha;
    case 'two':
      return pLeft <= alpha / 2 || pRight <= alpha / 2;
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Region annotation series (scatter points with labels)
// ---------------------------------------------------------------------------

function buildAnnotationSeries(
  criticalValues: readonly number[],
  tailType: string,
  alpha: number,
  xMin: number,
  xMax: number,
  peakY: number,
): Array<Record<string, unknown>> {
  if (criticalValues.length === 0) {
    return [];
  }

  const sorted = [...criticalValues].sort((a, b) => a - b);
  const annotations: Array<Record<string, unknown>> = [];
  const rejectLabelY = peakY * 0.18;
  const acceptLabelY = peakY * 0.45;

  if (tailType === 'two' && sorted.length === 2) {
    // Left rejection region
    const leftMidX = (xMin + sorted[0]) / 2;
    annotations.push(
      makeAnnotation(
        leftMidX,
        rejectLabelY,
        `Rejection Region\n\u03B1/2 = ${(alpha / 2).toFixed(3)}`,
        COLOR_CRITICAL_LINE,
      ),
    );

    // Acceptance region
    const centerX = (sorted[0] + sorted[1]) / 2;
    annotations.push(makeAnnotation(centerX, acceptLabelY, 'Acceptance Region', COLOR_LABEL_MUTED));

    // Right rejection region
    const rightMidX = (sorted[1] + xMax) / 2;
    annotations.push(
      makeAnnotation(
        rightMidX,
        rejectLabelY,
        `Rejection Region\n\u03B1/2 = ${(alpha / 2).toFixed(3)}`,
        COLOR_CRITICAL_LINE,
      ),
    );
  } else if (tailType === 'left') {
    const leftMidX = (xMin + sorted[0]) / 2;
    annotations.push(
      makeAnnotation(leftMidX, rejectLabelY, `Rejection Region\n\u03B1 = ${alpha}`, COLOR_CRITICAL_LINE),
    );

    const rightMidX = (sorted[0] + xMax) / 2;
    annotations.push(makeAnnotation(rightMidX, acceptLabelY, 'Acceptance Region', COLOR_LABEL_MUTED));
  } else {
    // right-tailed
    const cv = sorted[sorted.length - 1];
    const leftMidX = (xMin + cv) / 2;
    annotations.push(makeAnnotation(leftMidX, acceptLabelY, 'Acceptance Region', COLOR_LABEL_MUTED));

    const rightMidX = (cv + xMax) / 2;
    annotations.push(
      makeAnnotation(rightMidX, rejectLabelY, `Rejection Region\n\u03B1 = ${alpha}`, COLOR_CRITICAL_LINE),
    );
  }

  return annotations;
}

function makeAnnotation(x: number, y: number, text: string, color: string): Record<string, unknown> {
  return {
    type: 'scatter',
    data: [[x, y]],
    symbolSize: 0,
    label: {
      show: true,
      formatter: text,
      fontSize: 10,
      fontWeight: 500,
      color,
      backgroundColor: 'rgba(255,255,255,0.88)',
      padding: [3, 6],
      borderRadius: 3,
      lineHeight: 14,
    },
    z: 10,
    silent: true,
  };
}

// ---------------------------------------------------------------------------
// Split PDF curve into accept/reject series
// ---------------------------------------------------------------------------

function buildSplitSeries(
  allPoints: Array<[number, number]>,
  criticalValues: readonly number[],
  tailType: string,
  rejectColor = COLOR_PVALUE_AREA,
  curveColor = COLOR_CURVE,
): Array<Record<string, unknown>> {
  if (criticalValues.length === 0) {
    return [
      {
        type: 'line',
        data: allPoints,
        smooth: false,
        symbol: 'none',
        lineStyle: { color: curveColor, width: 1.5 },
        areaStyle: { color: COLOR_ACCEPT },
      },
    ];
  }

  const sorted = [...criticalValues].sort((a, b) => a - b);
  const series: Array<Record<string, unknown>> = [];

  if (tailType === 'two' && sorted.length === 2) {
    const leftReject = allPoints.filter(([x]) => x <= sorted[0]);
    const acceptRegion = allPoints.filter(([x]) => x >= sorted[0] && x <= sorted[1]);
    const rightReject = allPoints.filter(([x]) => x >= sorted[1]);

    series.push(makeAreaSeries(leftReject, rejectColor, curveColor, true));
    series.push(makeAreaSeries(acceptRegion, COLOR_ACCEPT, curveColor, false));
    series.push(makeAreaSeries(rightReject, rejectColor, curveColor, true));
  } else if (tailType === 'left') {
    const reject = allPoints.filter(([x]) => x <= sorted[0]);
    const accept = allPoints.filter(([x]) => x >= sorted[0]);

    series.push(makeAreaSeries(reject, rejectColor, curveColor, true));
    series.push(makeAreaSeries(accept, COLOR_ACCEPT, curveColor, false));
  } else {
    const cv = sorted[sorted.length - 1];
    const accept = allPoints.filter(([x]) => x <= cv);
    const reject = allPoints.filter(([x]) => x >= cv);

    series.push(makeAreaSeries(accept, COLOR_ACCEPT, curveColor, false));
    series.push(makeAreaSeries(reject, rejectColor, curveColor, true));
  }

  return series;
}

function makeAreaSeries(
  data: Array<[number, number]>,
  fillColor: string,
  lineColor: string,
  isRejectRegion: boolean,
): Record<string, unknown> {
  return {
    type: 'line',
    data,
    smooth: false,
    symbol: 'none',
    lineStyle: { color: lineColor, width: 1.5 },
    areaStyle: isRejectRegion
      ? { color: fillColor }
      : {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: fillColor },
            { offset: 1, color: fillColor.replace(/[\d.]+\)$/, '0.02)') },
          ]),
        },
  };
}
